import type { ProductPageContent } from '@/content/products';
import { accentBorder, accentSoftBg, accentText } from '@/lib/product-accents';
import { cn } from '@/lib/utils';

export function ProductInsights({ product }: { product: ProductPageContent }) {
  const hasContent =
    product.proof || product.statusDetail || product.namingNote || product.guardrail || product.partnersNote;

  if (!hasContent) {
    return null;
  }

  return (
    <section
      aria-labelledby={`${product.id}-insights-heading`}
      className="px-4 py-14 sm:px-6 sm:py-16 lg:px-8"
    >
      <div className="mx-auto grid w-full max-w-7xl gap-4 lg:grid-cols-2">
        <h2 id={`${product.id}-insights-heading`} className="sr-only">
          Additional product context
        </h2>

        {product.proof ? (
          <article className="border-border/70 bg-card/50 rounded-2xl border p-6 backdrop-blur-sm sm:p-8">
            <p className={cn('mb-2 text-xs font-medium tracking-[0.2em] uppercase', accentText[product.accent])}>
              Proof point
            </p>
            <h3 className="font-display text-foreground text-xl font-semibold tracking-tight">
              {product.proof.heading}
            </h3>
            <p className="text-muted-foreground mt-3 text-sm leading-relaxed sm:text-base">{product.proof.body}</p>
          </article>
        ) : null}

        {product.statusDetail ? (
          <article
            className={cn(
              'border-border/70 bg-card/50 rounded-2xl border p-6 backdrop-blur-sm sm:p-8',
              !product.proof && 'lg:col-span-2'
            )}
          >
            <p className={cn('mb-2 text-xs font-medium tracking-[0.2em] uppercase', accentText[product.accent])}>
              Status
            </p>
            <h3 className="font-display text-foreground text-xl font-semibold tracking-tight">
              {product.statusDetail.heading}
            </h3>
            <p className="text-muted-foreground mt-3 max-w-4xl text-sm leading-relaxed sm:text-base">
              {product.statusDetail.body}
            </p>
          </article>
        ) : null}

        {product.namingNote ? (
          <article className="border-border/70 bg-card/40 rounded-2xl border p-6 backdrop-blur-sm sm:p-8 lg:col-span-2">
            <p className={cn('mb-2 text-xs font-medium tracking-[0.2em] uppercase', accentText[product.accent])}>
              Formerly NextCure AIx
            </p>
            <h3 className="font-display text-foreground text-xl font-semibold tracking-tight">
              {product.namingNote.heading}
            </h3>
            <p className="text-muted-foreground mt-3 max-w-4xl text-sm leading-relaxed sm:text-base">
              {product.namingNote.body}
            </p>
          </article>
        ) : null}

        {product.guardrail ? (
          <article
            className={cn(
              'rounded-2xl border p-6 backdrop-blur-sm sm:p-8 lg:col-span-2',
              accentBorder[product.accent],
              accentSoftBg[product.accent]
            )}
          >
            <p className={cn('mb-2 text-xs font-medium tracking-[0.2em] uppercase', accentText[product.accent])}>
              Safeguard
            </p>
            <h3 className="font-display text-foreground text-xl font-semibold tracking-tight">
              {product.guardrail.heading}
            </h3>
            <p className="text-foreground/90 mt-3 max-w-4xl text-sm leading-relaxed sm:text-base">
              {product.guardrail.body}
            </p>
          </article>
        ) : null}

        {product.partnersNote ? (
          <aside className="border-border/70 bg-background/35 rounded-2xl border p-6 backdrop-blur-sm sm:p-8 lg:col-span-2">
            <p className={cn('mb-3 text-xs font-medium tracking-[0.2em] uppercase', accentText[product.accent])}>
              Partners
            </p>
            <p className="text-muted-foreground mt-3 max-w-4xl text-sm leading-relaxed sm:text-base">
              {product.partnersNote}
            </p>
          </aside>
        ) : null}
      </div>
    </section>
  );
}
